import { pdfHeaderText } from "./pdfTagName";
import { groupFuseCables } from "./fuseCableGroups";
import { groupFuseConsumers, type PositionedFuseNode } from "./fuseSymbolGroups";

export type RouteIcon = { data: string; width: number; height: number };
export type RouteShape =
  | { kind: "line"; x: number; y: number; x2: number; y2: number; color: string; dashed?: boolean; strokeWidth?: number }
  | { kind: "rect"; x: number; y: number; width: number; height: number; radius?: number; fill?: string; dashed?: boolean }
  | { kind: "ellipse"; x: number; y: number; rx: number; ry: number; fill?: string; stroke?: string; rightHalf?: boolean; strokeWidth?: number }
  | { kind: "text"; x: number; y: number; text: string; size: number; color: string; anchor: "start" | "middle"; width: number; primaryConsumerTag?: string; weight?: "normal" | "bold" }
  | { kind: "image"; x: number; y: number; width: number; height: number; source: string };
export type ConnectionRouteStyle = {
  encloseIntermediateCaptions?: boolean;
  scale?: number;
  measure: (text: string, size: number, weight?: "normal" | "bold") => number;
  lines: (text: string, width: number, size: number) => string[];
  icons?: ReadonlyMap<string, RouteIcon>;
  connectAdjacent?: boolean;
  /** Space above a grouped consumer, in unscaled scene units. */
  groupTitleGap?: number;
  /** Compact terminal-plan frames reserve room for the outgoing cable sleeve. */
  compactConsumerGroups?: boolean;
  /** Converts physical PDF-point offsets to the host scene's coordinate system. */
  unitsPerPoint?: number;
};
const ink = "#172b3a", muted = "#586a75";
const cableStroke = "#8798a2";
const cableDetailScale = 0.7;

/** Uses preloaded PDF images or the original source for browser rendering. */
function consumerIcon(source: string | undefined, style: ConnectionRouteStyle): RouteIcon | undefined {
  if (!source) return undefined;
  return style.icons ? style.icons.get(source) : { data: source, width: 24, height: 24 };
}

/** Measures the conductor badge so the cable jacket can end just beyond it. */
function conductorBubbleWidth(item: PositionedFuseNode, style: ConnectionRouteStyle): number {
  const scale = (style.scale ?? 1) * cableDetailScale;
  return Math.max(28 * scale, style.measure(item.node.label, 6 * scale) + 8 * scale);
}

/** Reserves a measured tag corridor before the cable, with identical physical clearance in both plans. */
export function primaryConsumerSpace(tag: string, style: ConnectionRouteStyle): number {
  const units = style.unitsPerPoint ?? 1;
  return Math.max(24 * units, style.measure(tag, 8 * (style.scale ?? 1), "bold") + 12 * units);
}

/** Draws primary tags once per adjacent terminal block, repeating them on each page. */
function primaryConsumerShapes(nodes: PositionedFuseNode[], style: ConnectionRouteStyle): RouteShape[] {
  const shapes: RouteShape[] = [];
  const groups: PositionedFuseNode[][] = [];
  const active = new Map<number, PositionedFuseNode[]>();
  for (const item of nodes) {
    if (!item.node.primaryConsumer) continue;
    let group = active.get(item.x);
    const previous = group?.at(-1);
    if (!previous || previous.endsConsumerBlock || previous.row !== item.row - 1 ||
        previous.node.primaryConsumer?.id !== item.node.primaryConsumer.id) {
      group = [];
      groups.push(group);
      active.set(item.x, group);
    }
    group!.push(item);
  }
  const units = style.unitsPerPoint ?? 1;
  for (const group of groups) {
    const first = group[0], last = group[group.length - 1];
    const tag = first.node.primaryConsumer!.tag;
    const space = primaryConsumerSpace(tag, style);
    const width = space - 8 * units;
    const x = first.x - space;
    const y = (first.y + last.y) / 2;
    const scale = style.scale ?? 1;
    const textHeight = Math.max(10 * units, 12 * scale);
    shapes.push({ kind: "rect", x, y: y - textHeight / 2, width, height: textHeight, fill: "#fff" });
    shapes.push({ kind: "text", text: tag, primaryConsumerTag: tag, x: x + width / 2,
      y: y + 3 * scale, width, size: 8 * scale, color: ink, anchor: "middle", weight: "bold" });
  }
  return shapes;
}

/** Moves primary tags out of endpoint captions without changing the supplied route model. */
function withoutPrimaryConsumerLabels(nodes: PositionedFuseNode[]): PositionedFuseNode[] {
  const primaryIds = new Set<string>();
  for (const item of nodes) {
    if (item.node.primaryConsumer) primaryIds.add(`${item.row}:${item.node.primaryConsumer.id}`);
  }
  return nodes.map(item => item.node.kind === "consumer" && primaryIds.has(`${item.row}:${item.node.consumerId ?? item.node.key}`)
    ? { ...item, node: { ...item.node, label: "" } } : item);
}

/** Supplies the same physical sizes and single-line layout to the SVG and PDF adapters. */
export function createPlanConnectionRouteStyle(measure: ConnectionRouteStyle["measure"], unitsPerPoint = 1, connectAdjacent = false): ConnectionRouteStyle {
  return {
    measure, unitsPerPoint, connectAdjacent, compactConsumerGroups: true,
    scale: 1.625 * (595.28 - 90) / 900 * unitsPerPoint,
    lines: (text, width, size) => {
      if (measure(text, size) <= width) return [text];
      if (measure("…", size) > width) return [""];
      const chars = Array.from(text);
      let low = 0, high = chars.length;
      while (low < high) {
        const middle = Math.ceil((low + high) / 2);
        if (measure(chars.slice(0, middle).join("") + "…", size) <= width) low = middle;
        else high = middle - 1;
      }
      return [chars.slice(0, low).join("") + "…"];
    },
  };
}

/** Uses the conductor colour consistently in SVG and PDF. */
export function routeColor(color?: string): { fill: string; text: string } {
  const fill = /^#[0-9a-f]{6}([0-9a-f]{2})?$/i.test(color || "") ? color!.slice(0, 7) : "#edf1f4";
  const rgb = parseInt(fill.slice(1), 16);
  const light = ((rgb >> 16) * 299 + ((rgb >> 8) & 255) * 587 + (rgb & 255) * 114) / 1000;
  return { fill, text: light < 150 ? "#ffffff" : ink };
}

/** Adds measured multiline text to the renderer-independent vector scene. */
function textLines(shapes: RouteShape[], text: string, x: number, y: number, width: number, size: number, color: string, style: ConnectionRouteStyle, anchor: "start" | "middle" = "middle"): number {
  const lines = style.lines(text, Math.max(0, width), size);
  for (const [index, line] of lines.entries()) shapes.push({ kind: "text", x, y: y + index * size * 1.25, text: line, width, size, color, anchor });
  return lines.length * size * 1.25;
}

/** Places identity beside the endpoint, with the room and floor on a separate line. */
function consumerCaption(shapes: RouteShape[], item: PositionedFuseNode, x: number, y: number, width: number, style: ConnectionRouteStyle): void {
  const s = style.scale ?? 1;
  const caption = item.node.caption;
  const title = caption ? pdfHeaderText(caption) : pdfHeaderText({ tag: item.node.label, name: item.node.name });
  textLines(shapes, title, x, y + 3 * s, width, 8 * s, ink, style, "start");
  if (caption?.location) textLines(shapes, caption.location, x, y + 17 * s, width, 7 * s, muted, style, "start");
  if (item.node.detail) textLines(shapes, item.node.detail, x, y + (caption?.location ? 28 : 14) * s, width, 6 * s, muted, style, "start");
}

/** Restores the narrow, cleared sleeve and elbow caption for a single conductor. */
function singleWireCableShapes(shapes: RouteShape[], item: PositionedFuseNode, label: string, right: number, style: ConnectionRouteStyle): void {
  const s = style.scale ?? 1;
  const x = item.x + 3 * s;
  const leaderY = item.y + 17 * s;
  shapes.push({ kind: "rect", x: x - 5 * s, y: item.y - 9 * s, width: 10 * s, height: 18 * s, fill: "#fff" });
  shapes.push({ kind: "ellipse", x, y: item.y, rx: 3 * s, ry: 7 * s, stroke: cableStroke });
  shapes.push({ kind: "line", x, y: item.y + 7 * s, x2: x, y2: leaderY, color: cableStroke });
  shapes.push({ kind: "line", x, y: leaderY, x2: x + 12 * s, y2: leaderY, color: cableStroke });
  const captionX = x + 15 * s;
  textLines(shapes, label, captionX, leaderY + 3 * s, Math.min(item.cableLabelWidth ?? item.width, right - captionX), 6 * s, muted, style, "start");
}

/** Lays out shared wire bubbles, consumer groups and cable sleeves once for both render targets. */
export function buildConnectionRouteScene(nodes: PositionedFuseNode[], style: ConnectionRouteStyle): RouteShape[] {
  nodes = withoutPrimaryConsumerLabels(nodes);
  const shapes: RouteShape[] = [];
  const s = style.scale ?? 1;
  const line = (x: number, y: number, x2: number, y2: number, color = ink) => shapes.push({ kind: "line", x, y, x2, y2, color });
  if (style.connectAdjacent) {
    let previous: PositionedFuseNode | undefined;
    for (const item of nodes) {
      if (previous && previous.row === item.row && previous.continues) line(previous.x + previous.width, previous.y, item.x, item.y);
      previous = item;
    }
  }
  let contentRight = 0;
  for (const item of nodes) contentRight = Math.max(contentRight, item.x + item.width);
  const cableGroups = groupFuseCables(nodes);
  const cableLayouts = new Map<PositionedFuseNode, { entryX: number; exitX: number; radius: number; exitRadius: number }>();
  for (const group of cableGroups) {
    if (group.items.length === 1) continue;
    const first = group.items[0];
    const units = style.unitsPerPoint ?? 1;
    const space = first.node.primaryConsumer ? primaryConsumerSpace(first.node.primaryConsumer.tag, style) : 0;
    const radius = space ? (space - 8 * units) / 2 + 2 * units : 4 * s;
    const exitRadius = radius;
    const entryX = space ? first.x - space / 2 - 4 * units : first.x + 7 * s;
    let bubbleWidth = 0;
    for (const item of group.items) bubbleWidth = Math.max(bubbleWidth, conductorBubbleWidth(item, style));
    const previousLength = first.x + (first.width + bubbleWidth) / 2 + 4 * s - entryX;
    const availableLength = first.x + first.width - exitRadius - 4 * s - entryX;
    const minimumLength = bubbleWidth + radius + exitRadius + 8 * s;
    const length = Math.max(minimumLength, Math.min(previousLength * 0.7, availableLength));
    const layout = { entryX, exitX: entryX + length, radius, exitRadius };
    for (const item of group.items) cableLayouts.set(item, layout);
  }
  const grouped = new Set<PositionedFuseNode>();
  for (const group of groupFuseConsumers(nodes)) {
    const first = group[0], last = group[group.length - 1];
    const compact = style.compactConsumerGroups ?? false;
    const frameWidth = compact ? 40 * s : first.width;
    const frameTop = first.y - 14 * s;
    const frameBottom = last.y + 14 * s;
    const frameIndex = shapes.length;
    shapes.push({ kind: "rect", x: first.x, y: frameTop, width: frameWidth, height: frameBottom - frameTop, dashed: true });
    let captionBounds: { top: number; bottom: number; right: number } | undefined;
    let iconDrawn = false;
    let captionDrawn = false;
    for (const item of group) {
      grouped.add(item);
      const center = item.x + frameWidth / 2;
      const icon = consumerIcon(item.node.icon, style);
      if (item.continues) {
        line(item.x, item.y, item.x + item.width, item.y);
        shapes.push({ kind: "ellipse", x: center, y: item.y, rx: 2.5 * s, ry: 2.5 * s, fill: ink });
      } else if (icon && !iconDrawn && !item.node.detail) {
        const scale = 18 * s / Math.max(icon.width, icon.height);
        const width = icon.width * scale, height = icon.height * scale;
        line(item.x, item.y, center - width / 2, item.y);
        shapes.push({ kind: "image", x: center - width / 2, y: item.y - height / 2, width, height, source: icon.data });
        iconDrawn = true;
      } else {
        line(item.x, item.y, center, item.y);
        if (!item.node.detail) shapes.push({ kind: "ellipse", x: center, y: item.y, rx: 2.5 * s, ry: 2.5 * s, fill: "#fff", stroke: ink });
      }
      const directional = item.node.detail === "Up" || item.node.detail === "Down";
      if (!captionDrawn && !item.continues && (!item.node.detail || directional)) {
        let captionX = first.x + frameWidth + 12 * s;
        // Direction labels remain at their wire endpoint; identity sits beside the frame.
        if (directional && icon) {
          const scale = 18 * s / Math.max(icon.width, icon.height);
          const width = icon.width * scale, height = icon.height * scale;
          shapes.push({ kind: "image", x: captionX + 9 * s - width / 2, y: item.y - height / 2, width, height, source: icon.data });
          captionX += 24 * s;
          iconDrawn = true;
        }
        const captionStart = shapes.length;
        const enclose = style.encloseIntermediateCaptions && group.some(entry => entry.continues);
        const captionRight = item.captionWidth === undefined ? contentRight : item.x + item.captionWidth;
        consumerCaption(shapes, directional ? { ...item, node: { ...item.node, detail: undefined } } : item,
          captionX, item.y, captionRight - captionX - (enclose ? 8 * s : 0), style);
        if (enclose) {
          const texts = shapes.slice(captionStart).filter((shape): shape is Extract<RouteShape, { kind: "text" }> => shape.kind === "text");
          if (texts.length) captionBounds = {
            top: Math.max(frameTop, item.y - 14 * s),
            bottom: Math.min(frameBottom, Math.max(...texts.map(text => text.y)) + 8 * s),
            right: Math.max(...texts.map(text => text.x + style.measure(text.text, text.size, text.weight))) + 8 * s,
          };
        }
        captionDrawn = true;
      }
      if (item.node.detail) textLines(shapes, item.node.detail, center, item.y + (compact ? 10 : 14) * s, frameWidth - 8 * s, 6 * s, muted, style);
    }
    if (captionBounds) {
      const right = first.x + frameWidth;
      const points = [[first.x, frameTop], [right, frameTop], [right, captionBounds.top],
        [captionBounds.right, captionBounds.top], [captionBounds.right, captionBounds.bottom],
        [right, captionBounds.bottom], [right, frameBottom], [first.x, frameBottom], [first.x, frameTop]];
      const outline: RouteShape[] = [];
      for (let i = 1; i < points.length; i++) {
        const [x, y] = points[i - 1], [x2, y2] = points[i];
        if (x !== x2 || y !== y2) outline.push({ kind: "line", x, y, x2, y2, color: muted, dashed: true });
      }
      shapes.splice(frameIndex, 1, ...outline);
    }
  }
  for (const item of nodes) {
    const { node, x, y, width } = item;
    if (node.kind === "wire") {
      const colors = routeColor(node.color);
      const wireScale = s * cableDetailScale;
      const bubble = conductorBubbleWidth(item, style);
      const cable = cableLayouts.get(item);
      const bubbleCenter = cable
        ? Math.max((cable.entryX + cable.exitX) / 2, cable.entryX + cable.radius + 4 * s + bubble / 2)
        : x + width / 2;
      const bx = bubbleCenter - bubble / 2;
      const lineCount = style.lines(node.label, Math.max(0, bubble - 8 * wireScale), 6 * wireScale).length;
      const bubbleHeight = Math.max(12 * wireScale, lineCount * 7.5 * wireScale + 4 * wireScale);
      line(x, y, bx, y, cableStroke); line(bx + bubble, y, x + width, y, "#000");
      if (node.label) shapes.push({ kind: "rect", x: bx, y: y - bubbleHeight / 2, width: bubble, height: bubbleHeight, radius: 6 * wireScale, fill: colors.fill });
      else line(bx, y, bx + bubble, y, cableStroke);
      textLines(shapes, node.label, bubbleCenter, y + 2 * wireScale - (lineCount - 1) * 3.75 * wireScale, bubble - 8 * wireScale, 6 * wireScale, colors.text, style);
    } else if (node.kind === "consumer" && !grouped.has(item)) {
      const icon = consumerIcon(node.icon, style);
      if (!icon && !item.continues) {
        shapes.push({ kind: "ellipse", x, y, rx: 2.5 * s, ry: 2.5 * s, fill: "#fff", stroke: ink });
      }
      if (icon) {
        const scale = 18 * s / Math.max(icon.width, icon.height);
        const iw = icon.width * scale, ih = icon.height * scale;
        shapes.push({ kind: "image", x: x + 9 * s - iw / 2, y: y - ih / 2, width: iw, height: ih, source: icon.data });
      }
      const inset = icon ? 24 * s : 8 * s;
      consumerCaption(shapes, item, x + inset, y, (item.captionWidth ?? width) - inset - 4 * s, style);
    } else if (node.kind === "note") textLines(shapes, node.label, x + width / 2, y, width - 8 * s, 7 * s, muted, style);
  }
  for (const group of cableGroups) {
    const first = group.items[0], last = group.items[group.items.length - 1];
    if (group.items.length === 1) {
      singleWireCableShapes(shapes, first, group.label, contentRight, style);
      continue;
    }
    const { entryX, exitX, radius: entryRadius, exitRadius } = cableLayouts.get(first)!;
    const centerY = (first.y + last.y) / 2;
    const radiusY = (last.y - first.y) / 2 + 10 * s;
    const bottom = centerY + radiusY;
    // Open ends and the two jacket edges keep the individual conductors visible.
    shapes.push({ kind: "ellipse", x: entryX, y: centerY, rx: entryRadius, ry: radiusY, stroke: cableStroke });
    // At the exit the jacket takes precedence: clear the conductors along its curve.
    shapes.push({ kind: "ellipse", x: exitX, y: centerY, rx: exitRadius, ry: radiusY, rightHalf: true, stroke: "#fff", strokeWidth: 3 * s });
    shapes.push({ kind: "ellipse", x: exitX, y: centerY, rx: exitRadius, ry: radiusY, rightHalf: true, stroke: cableStroke });
    line(entryX, centerY - radiusY, exitX, centerY - radiusY, cableStroke);
    line(entryX, bottom, exitX, bottom, cableStroke);
    // Inside the cable, the right half of the entrance ellipse also takes precedence.
    shapes.push({ kind: "ellipse", x: entryX, y: centerY, rx: entryRadius, ry: radiusY,
      rightHalf: true, stroke: "#fff", strokeWidth: 3 * s });
    shapes.push({ kind: "ellipse", x: entryX, y: centerY, rx: entryRadius, ry: radiusY,
      rightHalf: true, stroke: cableStroke });
    for (const item of group.items) {
      const radial = Math.sqrt(Math.max(0, 1 - ((item.y - centerY) / radiusY) ** 2));
      const crossing = entryX - entryRadius * radial;
      // Clear the jacket at the actual ellipse intersection, then restore the thin conductor.
      shapes.push({ kind: "line", x: crossing - 3 * s, y: item.y, x2: crossing + 3 * s, y2: item.y,
        color: "#fff", strokeWidth: 3 * s });
      line(crossing - 3 * s, item.y, crossing + 3 * s, item.y, ink);
    }
    const captionWidth = Math.max(first.width, exitX - entryX);
    const captionX = (entryX + exitX) / 2;
    textLines(shapes, group.label, captionX, bottom + 9 * s, captionWidth, 6 * s, muted, style);
  }
  shapes.push(...primaryConsumerShapes(nodes, style));
  return shapes;
}
